import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, BookOpen, Calendar } from "lucide-react";
import { setCanonical } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { slug } = useParams();

  if (slug) return <BlogPost slug={slug} />;
  return <BlogListing />;
}

function BlogListing() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog | Sound Therapy Articles | ToneHealing";
    setCanonical("/blog");
    axios.get(`${API}/blog/posts`).then(r => {
      setPosts(r.data.posts || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="pt-24 pb-16" data-testid="blog-listing">
      {/* Hero with meditation globe */}
      <div className="relative overflow-hidden pb-12" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-bg-2.png)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <Link to="/" className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:text-primary/80 mb-6 transition-colors bg-white/60 px-4 py-2 rounded-full">
                <ArrowLeft className="w-5 h-5" /> Home
              </Link>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4" data-testid="blog-title">Sound Therapy Blog</h1>
              <p className="text-muted-foreground max-w-xl">Explore the science and practice of sound wellness, binaural beats, and frequency therapy.</p>
            </div>
            <div className="hidden lg:flex items-center justify-center relative">
              <div className="absolute w-40 h-40 rounded-full border-2 border-primary/20" style={{ animation: 'ring-pulse-1 3s ease-in-out infinite' }} />
              <div className="absolute w-52 h-52 rounded-full border border-primary/10" style={{ animation: 'ring-pulse-2 4s ease-in-out infinite 0.5s' }} />
              <img src="https://static.prod-images.emergentagent.com/jobs/e586dc18-7e49-4762-9a32-fdcca74463b6/images/4934b7f9e8db17a37a56475081896b2c65939f6c0ba1eb7b6972c30393fe8a80.png" alt="Sound therapy meditation" className="relative z-10 w-36 h-36 object-cover rounded-full drop-shadow-2xl animate-wave-float" />
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">

        {loading ? (
          <div className="text-muted-foreground animate-pulse">Loading articles...</div>
        ) : posts.length === 0 ? (
          <div className="glass-light rounded-2xl p-12 text-center">
            <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">Coming Soon</h3>
            <p className="text-sm text-muted-foreground">Our first articles are being written. Check back soon for sound therapy insights and guides.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="block glass-light rounded-2xl p-6 sm:p-8 hover:bg-muted/20 transition-all group" data-testid={`blog-card-${post.slug}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="outline" className="text-xs">{post.category || "Sound Therapy"}</Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {post.title}
                </h2>
                <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt?.split('\n').filter(line => !line.startsWith('#') && line.trim() !== post.title?.trim() && line.trim()).join(' ').replace(/\*{1,2}/g, '').trim().slice(0, 200)}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BlogPost({ slug }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/posts/${slug}`).then(r => {
      setPost(r.data.post);
      document.title = `${r.data.post.title} | ToneHealing Blog`;
      setCanonical(`/blog/${slug}`);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-16"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!post) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-16 gap-4">
      <p className="text-muted-foreground">Article not found</p>
      <Link to="/blog"><Button variant="outline">Back to Blog</Button></Link>
    </div>
  );

  const linkifyText = (text) => {
    // Convert markdown links [text](url) to clickable links
    const parts = text.split(/(\[[^\]]+\]\([^\)]+\))/g);
    return parts.map((part, j) => {
      const mdMatch = part.match(/\[([^\]]+)\]\(([^\)]+)\)/);
      if (mdMatch) return <a key={j} href={mdMatch[2]} className="text-primary hover:underline" target={mdMatch[2].startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer">{mdMatch[1]}</a>;
      // Auto-link ToneHealing.com mentions
      const thParts = part.split(/(ToneHealing\.com|tonehealing\.com)/gi);
      return thParts.map((tp, k) => 
        tp.toLowerCase() === 'tonehealing.com' 
          ? <a key={`${j}-${k}`} href="https://tonehealing.com" className="text-primary hover:underline font-medium">ToneHealing.com</a>
          : tp
      );
    });
  };

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-bold mt-8 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{linkifyText(line.replace('### ', ''))}</h3>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold mt-10 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{linkifyText(line.replace('## ', ''))}</h2>;
      if (line.startsWith('- ')) return <li key={i} className="text-muted-foreground ml-6 mb-2 list-disc">{linkifyText(line.replace('- ', ''))}</li>;
      if (line.startsWith('* ')) return <li key={i} className="text-muted-foreground ml-6 mb-2 list-disc">{linkifyText(line.replace('* ', ''))}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-muted-foreground leading-relaxed mb-4">{linkifyText(line)}</p>;
    });
  };

  const relatedConditions = [
    { name: "Anxiety", slug: "anxiety" },
    { name: "Sleep", slug: "sleep" },
    { name: "Focus", slug: "focus" },
    { name: "Pain Relief", slug: "pain" },
    { name: "Stress", slug: "stress" },
    { name: "Meditation", slug: "meditation" },
    { name: "Depression", slug: "depression" },
    { name: "ADHD", slug: "adhd" },
  ];

  return (
    <div className="pt-24 pb-16" data-testid="blog-post">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> All Articles
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <Badge variant="outline">{post.category || "Sound Therapy"}</Badge>
          <span className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8" data-testid="blog-post-title">{post.title}</h1>
        <div className="blog-content">{renderContent(post.content)}</div>

        {/* CTA */}
        <div className="mt-12 glass rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Ready to Try Sound Therapy?</h3>
          <p className="text-sm text-muted-foreground mb-4">Experience the wellness benefits of binaural beats and Solfeggio frequencies — free, no account required.</p>
          <Link to="/"><Button className="gap-2">Start Your Session <ArrowLeft className="w-4 h-4 rotate-180" /></Button></Link>
        </div>

        {/* Internal Links — SEO */}
        <div className="mt-8 rounded-2xl p-6 bg-secondary/30 border border-border/30" data-testid="blog-related-therapies">
          <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Explore Sound Therapy on <a href="https://tonehealing.com" className="text-primary hover:underline">ToneHealing.com</a></h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {relatedConditions.map(c => (
              <Link key={c.slug} to={`/therapy/${c.slug}`} className="text-sm text-primary hover:underline px-3 py-2 rounded-lg bg-card/60 text-center transition-colors hover:bg-primary/5">
                {c.name}
              </Link>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            <a href="https://tonehealing.com" className="text-primary hover:underline">ToneHealing.com</a> offers free, science-backed sound therapy for 50+ health conditions. Try binaural beats, Solfeggio frequencies, and ambient soundscapes — no account needed.
          </p>
        </div>

        {/* Medical Disclaimer */}
        <div className="mt-8 rounded-2xl p-6 bg-amber-50 border border-amber-200" data-testid="blog-medical-disclaimer">
          <p className="text-xs text-amber-900 leading-relaxed">
            <strong>Medical & Legal Disclaimer:</strong> This content is for informational and wellness purposes only. It is not intended to diagnose, treat, cure, or prevent any medical condition. Sound therapy, binaural beats, and Solfeggio frequencies are complementary wellness tools and are not a substitute for professional medical advice, diagnosis, or treatment. <strong>Users assume full responsibility for safe usage.</strong> Do not use while driving, operating machinery, or during activities requiring alertness. Consult healthcare professionals for serious health concerns. Individual results may vary. Contact: <a href="mailto:support@lifeownit.com" className="underline">support@lifeownit.com</a>
          </p>
        </div>

        {/* Safety Warning */}
        <div className="mt-4 rounded-2xl p-5 bg-red-50 border border-red-200" data-testid="blog-safety-warning">
          <p className="text-xs text-red-800 leading-relaxed">
            <strong>Important Safety Warning:</strong> DO NOT use this sound therapy while driving, operating machinery, or during any activity requiring alertness and attention. These frequencies are designed to alter brainwave patterns and may cause drowsiness, relaxation, or altered states of consciousness. Use only in a safe, stationary environment. Not recommended for individuals with epilepsy, seizure disorders, or hearing impairments without medical consultation.
          </p>
        </div>

        {/* Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "datePublished": post.created_at,
          "publisher": { "@type": "Organization", "name": "ToneHealing", "url": "https://tonehealing.com" },
          "description": post.excerpt,
        })}} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://tonehealing.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://tonehealing.com/blog" },
            { "@type": "ListItem", "position": 3, "name": post.title },
          ]
        })}} />
      </article>
    </div>
  );
}
